import logger from '../services/logger'
import { getToken } from '../utils/getToken'
import { setRoleCapabilities } from '../utils/Permissions'

export const GET_USER_PROFILE_BEGIN = 'GET_USER_PROFILE_BEGIN'
export const GET_USER_PROFILE_SUCCESS = 'GET_USER_PROFILE_SUCCESS'
export const GET_USER_PROFILE_FAILURE = 'GET_USER_PROFILE_FAILURE'
export const GET_SETTINGS_BEGIN = 'GET_SETTINGS_BEGIN'
export const GET_SETTINGS_SUCCESS = 'GET_SETTINGS_SUCCESS'
export const GET_SETTINGS_FAILURE = 'GET_SETTINGS_FAILURE'
export const GET_CAPABILITIES_BEGIN = 'GET_CAPABILITIES_BEGIN'
export const GET_CAPABILITIES_SUCCESS = 'GET_CAPABILITIES_SUCCESS'
export const GET_CAPABILITIES_FAILURE = 'GET_CAPABILITIES_FAILURE'

export const getUserProfileBegin = response => ({
  type: GET_USER_PROFILE_BEGIN,
})

export const getUserProfileSuccess = response => ({
  type: GET_USER_PROFILE_SUCCESS,
  payload: response,
})

export const getUserProfileFailure = error => ({
  type: GET_USER_PROFILE_FAILURE,
  error,
})

export const getSettingsSuccess = response => ({
  type: GET_SETTINGS_SUCCESS,
  settings: response,
})

export const getSettingsFailure = error => ({
  type: GET_SETTINGS_FAILURE,
  error,
})

export const getCapabilitiesSuccess = response => ({
  type: GET_CAPABILITIES_SUCCESS,
  capabilities: response,
})

export const getCapabilitiesFailure = error => ({
  type: GET_CAPABILITIES_FAILURE,
  error,
})

export const RESET_ERRORS = 'RESET_ERRORS'

export function beginLoading() {
  return dispatch => {
    // Reset any stale errors from a previous session before loading
    // (e.g. "Took too long to get user profile" left over from the /signin page).
    dispatch({ type: RESET_ERRORS })
    // Signal that loading has started so the spinner shows and the error
    // panel from the previous session doesn't flash before data arrives.
    dispatch(getUserProfileBegin())
    getUserProfile(dispatch)
    getSettings(dispatch)
    getCapabilities(dispatch)
  }
}

function getUserProfile(dispatch) {
  // If there's no token the user isn't logged in — skip the API call entirely.
  // Without this guard, landing on /signin after a session timeout causes a
  // 30-second, 30-retry 504 storm (the API container may be cold) that ends
  // with "Took too long to get user profile" in the Redux error state, which
  // then blocks the main app after the user successfully signs back in.
  if (!getToken()) {
    dispatch(getUserProfileFailure(new Error('No token available')))
    return
  }

  let getProfileAttempt = 0

  getProfile(dispatch)

  function getProfile(dispatch) {
    fetch('/api/profile', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    })
      .then(async response => {
        if (response.status === 504 /* gateway timeout */) {
          getProfileAttempt++

          if (getProfileAttempt > 30) throw Error('Took too long to get user profile')

          logger.info('Get profile timed out. Retrying...')

          setTimeout(() => getProfile(dispatch), 1000)
          return
        }

        if (!response.ok) {
          if (response.status === 401) {
            const returnTo = encodeURIComponent(window.location.pathname + window.location.search)
            window.location.replace(`/signin?returnTo=${returnTo}`)
            return
          }

          if (response.status === 403) {
            throw Error('User is not authorized to use this tenant')
          }

          throw Error(response.statusText)
        }
        const payload = await response.json()
        dispatch(getUserProfileSuccess(payload.user))
      })
      .catch(error => dispatch(getUserProfileFailure(error)))
  }
}

function getSettings(dispatch) {
  let getProfileAttempt = 0

  getSettingsInner(dispatch)

  function getSettingsInner(dispatch) {
    fetch('/api/settings')
      .then(async response => {
        if (response.status === 504 /* gateway timeout */) {
          getProfileAttempt++

          if (getProfileAttempt > 30) throw Error('Took too long to get settings')

          logger.info('Get settings timed out. Retrying...')

          setTimeout(() => getSettingsInner(dispatch), 1000)
          return
        }

        if (!response.ok) {
          throw Error(response.statusText)
        }

        const settings = await response.json()
        dispatch(getSettingsSuccess(settings))
      })
      .catch(error => dispatch(getSettingsFailure(error)))
  }
}

function getCapabilities(dispatch) {
  fetch('/api/auth/configuration')
    .then(async response => {
      if (!response.ok) {
        throw Error(response.statusText)
      }
      const data = await response.json()
      // Initialize Permissions utility
      setRoleCapabilities(data)
      dispatch(getCapabilitiesSuccess(data))
    })
    .catch(error => dispatch(getCapabilitiesFailure(error)))
}
